import { CaretDownIcon, CheckIcon, GlobeIcon } from '@phosphor-icons/react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  toSupportedLocale,
  type SupportedLocale,
} from '@shared/i18n';
import { Button } from '@shared/ui';

import styles from './LanguageMenu.module.css';

const LAST_INDEX = SUPPORTED_LOCALES.length - 1;

/** Код языка на кнопке и в пункте меню: «DE», «RU», «EN». */
function code(locale: SupportedLocale): string {
  return locale.toUpperCase();
}

/**
 * Выбор языка интерфейса (DESIGN_SYSTEM §4.3): глобус, код текущего языка и
 * стрелка на тёмной шапке, под ним — светлый список из трёх локалей.
 *
 * Компонент не знает, в какой шапке стоит: публичной сейчас, шапке `/app/*` —
 * со следующей вехи. Обе тёмные, поэтому кнопка всегда в варианте `inverse`.
 *
 * Поведение — кнопка меню WAI-ARIA: при открытии фокус встаёт на текущий язык,
 * стрелки, Home и End ходят по пунктам, Enter и пробел выбирают, Escape
 * закрывает и возвращает фокус на кнопку, Tab и клик снаружи — просто
 * закрывают. Пункты — `menuitemradio`: язык выбирается один из трёх, и
 * скринридер обязан сказать, какой выбран сейчас.
 *
 * Выбор сохраняет сам детектор языка (`caches: ['localStorage']`), а
 * `<html lang>` переключает подписка в `initI18n` — здесь ни то ни другое не
 * дублируется.
 */
export function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const current = toSupportedLocale(i18n.resolvedLanguage ?? i18n.language);
  const currentIndex = SUPPORTED_LOCALES.indexOf(current);

  const [open, setOpen] = useState(false);
  // Какой пункт держит фокус, пока меню открыто: фокус двигает эффект ниже,
  // клавиши меняют только индекс.
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const triggerId = useId();
  const menuId = useId();

  const openAt = (index: number) => {
    setActiveIndex(index);
    setOpen(true);
  };

  const close = (returnFocus: boolean) => {
    setOpen(false);
    // `Button` не пробрасывает ref, поэтому кнопка находится по своему id.
    if (returnFocus) document.getElementById(triggerId)?.focus();
  };

  useEffect(() => {
    if (open) itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  // Клик снаружи и Escape — как в меню аккаунта. Escape слушается на документе:
  // меню, открытое мышью, закрывается им и тогда, когда фокус остался на кнопке.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Фокус возвращается на кнопку, только если он был внутри меню: иначе
      // Escape уводил бы его оттуда, где человек сейчас находится.
      const focusInside = containerRef.current?.contains(document.activeElement) ?? false;
      setOpen(false);
      if (focusInside) document.getElementById(triggerId)?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, triggerId]);

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    // Enter и пробел открывают меню обычным кликом; стрелки — здесь.
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openAt(currentIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openAt(LAST_INDEX);
    }
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => (index === LAST_INDEX ? 0 : index + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => (index === 0 ? LAST_INDEX : index - 1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(LAST_INDEX);
        break;
      case 'Tab':
        // Фокус уходит своим путём, меню за ним не остаётся висеть.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const select = (locale: SupportedLocale) => {
    close(true);
    if (locale !== current) void i18n.changeLanguage(locale);
  };

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <Button
        id={triggerId}
        variant="inverse"
        className={styles.trigger}
        // Видимая подпись — только код («DE»), поэтому имя кнопки задано явно:
        // название языка для скринридера и тот же код в конце — голосовое
        // управление находит кнопку по тому, что видно (WCAG 2.5.3).
        aria-label={t('nav.languageMenu.trigger', {
          language: LOCALE_LABELS[current],
          code: code(current),
        })}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (open) close(true);
          else openAt(currentIndex);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={styles.triggerContent}>
          <GlobeIcon size={16} aria-hidden="true" />
          <span className={styles.triggerCode}>{code(current)}</span>
          <CaretDownIcon
            size={14}
            weight="bold"
            aria-hidden="true"
            className={[styles.caret, open && styles.caretOpen].filter(Boolean).join(' ')}
          />
        </span>
      </Button>

      {open ? (
        <div
          id={menuId}
          className={styles.menu}
          role="menu"
          aria-label={t('nav.languageMenu.label')}
          onKeyDown={onMenuKeyDown}
        >
          {SUPPORTED_LOCALES.map((locale, index) => {
            const checked = locale === current;
            return (
              <button
                key={locale}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                tabIndex={-1}
                className={styles.item}
                onClick={() => {
                  select(locale);
                }}
              >
                <span className={styles.check} aria-hidden="true">
                  {checked ? <CheckIcon size={16} weight="bold" /> : null}
                </span>
                {/* Название языка — на самом языке и с его `lang`: «Русский»
                    в немецком интерфейсе произносится по-русски. */}
                <span lang={locale} className={styles.itemLabel}>
                  {LOCALE_LABELS[locale]}
                </span>
                <span className={styles.itemCode} aria-hidden="true">
                  {code(locale)}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
